import { useState, useEffect } from 'react'
import {
  ArchiveBoxIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  Bars3Icon,
  CheckCircleIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  CubeIcon,
  DocumentIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import {
  PackagingJob,
  JobArtifact,
  JOB_STATUS_LABELS,
  JOB_STATUS_TONES,
  jobDisplayName,
  isTerminalStatus,
  canCancelJob,
  canRetryJob,
  retryRunning,
} from '../types/job'
import { getJobArtifacts, downloadJobArtifact, cancelJob, retryJob, getArtifactContent } from '../services/api'
import { Badge, Modal, Spinner, Tabs, type IconComponent } from './ui'
import DecisionPanel from './DecisionPanel'
import { dateTime } from '../utils/date'
import { errorSummary } from '../utils/jobError'
import { jobPipeline, type PipelineStep } from '../utils/jobPipeline'
import { errorText } from '../utils/errorText'

interface JobDetailsProps {
  job: PackagingJob | null
  isOpen: boolean
  onClose: () => void
  onJobUpdated: () => void
  /** Opens another job in this modal: the recovery job of a failed job, or the job it retries. */
  onOpenJob?: (jobId: string) => void
}

type TabType = 'pipeline' | 'details' | 'logs' | 'report'

/** Glyph per artifact kind, so a package, a report and a log are told apart at a glance. */
const ARTIFACT_ICONS: Record<string, IconComponent> = {
  psadt_package: CubeIcon,
  intune_package: CubeIcon,
  full_package: ArchiveBoxIcon,
  report: DocumentTextIcon,
  log: Bars3Icon,
}

const SOURCE_LABELS: Record<string, string> = {
  winget: 'WinGet',
  chocolatey: 'Chocolatey',
  url: 'URL',
  file_upload: 'File Upload',
}

/**
 * ONE job in full, opened from the row's `Logs` button — the reference's job modal
 * (sap-mockup/app/(console)/jobs/page.tsx `JobLogsModal`): the job's identity in the title, its
 * source and timestamps as chips, then the run itself.
 *
 * It carries more than the reference's modal because our data is real: the reference derives a
 * fixed pipeline checklist from the status, where we have the actual execution log, the packaging
 * report and the artifacts the run produced. Those are the tabs.
 */
export default function JobDetails({ job, isOpen, onClose, onJobUpdated, onOpenJob }: JobDetailsProps) {
  const [artifacts, setArtifacts] = useState<JobArtifact[]>([])
  const [loadingArtifacts, setLoadingArtifacts] = useState(false)
  const [downloading, setDownloading] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('pipeline')
  const [logContent, setLogContent] = useState<string | null>(null)
  const [reportContent, setReportContent] = useState<string | null>(null)
  const [copiedToClipboard, setCopiedToClipboard] = useState(false)

  useEffect(() => {
    if (job && isOpen && isTerminalStatus(job.status)) {
      loadArtifacts()
    } else {
      setArtifacts([])
      setLogContent(null)
      setReportContent(null)
      setActiveTab('pipeline')
    }
  }, [job, isOpen])

  const loadArtifacts = async () => {
    if (!job) return

    setLoadingArtifacts(true)
    try {
      const response = await getJobArtifacts(job.job_id)
      setArtifacts(response.artifacts)

      // Auto-load log and report content if available
      const logArtifact = response.artifacts.find(a => a.artifact_type === 'log' && a.file_name === 'execution.log')
      const reportArtifact = response.artifacts.find(a => a.artifact_type === 'report')

      if (logArtifact) {
        loadArtifactContent(logArtifact.artifact_id, 'log')
      }
      if (reportArtifact) {
        loadArtifactContent(reportArtifact.artifact_id, 'report')
      }
    } catch (err) {
      console.error('Failed to load artifacts:', err)
    } finally {
      setLoadingArtifacts(false)
    }
  }

  const loadArtifactContent = async (artifactId: string, type: 'log' | 'report') => {
    if (!job) return

    try {
      const content = await getArtifactContent(job.job_id, artifactId)
      if (type === 'log') {
        setLogContent(content.content)
      } else {
        setReportContent(content.content)
      }
    } catch (err) {
      console.error(`Failed to load ${type} content:`, err)
    }
  }

  const handleDownload = async (artifact: JobArtifact) => {
    if (!job) return

    setDownloading(artifact.artifact_id)
    try {
      const blob = await downloadJobArtifact(job.job_id, artifact.artifact_id)

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = artifact.file_name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download failed:', err)
      setError('Failed to download artifact')
    } finally {
      setDownloading(null)
    }
  }

  const handleCancel = async () => {
    if (!job) return

    setActionLoading(true)
    setError(null)
    try {
      await cancelJob(job.job_id)
      onJobUpdated()
    } catch (err: any) {
      setError(errorText(err, 'Failed to cancel job'))
    } finally {
      setActionLoading(false)
    }
  }

  const handleRetry = async () => {
    if (!job) return

    setActionLoading(true)
    setError(null)
    try {
      await retryJob(job.job_id)
      onJobUpdated()
    } catch (err: any) {
      setError(errorText(err, 'Failed to retry job'))
    } finally {
      setActionLoading(false)
    }
  }

  const handleCopyLogs = async () => {
    if (!logContent) return

    try {
      await navigator.clipboard.writeText(logContent)
      setCopiedToClipboard(true)
      setTimeout(() => setCopiedToClipboard(false), 2000)
    } catch (err) {
      console.error('Failed to copy logs:', err)
      setError('Failed to copy logs to clipboard')
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (!isOpen || !job) return null

  const sourceLabel =
    job.source_type === 'winget' && job.learn_from === 'choco'
      ? 'Chocolatey'
      : SOURCE_LABELS[job.source_type] ?? job.source_type

  const tabs = [
    { key: 'pipeline', label: 'Pipeline' },
    { key: 'details', label: 'Details' },
    ...(logContent ? [{ key: 'logs', label: 'Execution Log' }] : []),
    ...(reportContent ? [{ key: 'report', label: 'Report' }] : []),
  ]

  /** Newest artifact per kind — a re-run leaves several, and only the last one is the package. */
  const packages = artifacts
    .filter((a) => ['psadt_package', 'intune_package', 'full_package'].includes(a.artifact_type))
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .reduce((unique: JobArtifact[], artifact) => {
      if (!unique.some((a) => a.artifact_type === artifact.artifact_type)) unique.push(artifact)
      return unique
    }, [])

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={`Job ${job.job_id.substring(0, 8)} — ${jobDisplayName(job)}`}
      size="xl"
      scrollKey={activeTab}
      footer={
        <>
          {canCancelJob(job.status) && (
            <button onClick={handleCancel} disabled={actionLoading} className="btn-danger">
              {actionLoading ? 'Cancelling…' : 'Cancel Job'}
            </button>
          )}
          {canRetryJob(job.status) && !retryRunning(job) && (
            <button onClick={handleRetry} disabled={actionLoading} className="btn-blue">
              {actionLoading ? 'Retrying…' : 'Retry Job'}
            </button>
          )}
          <button onClick={onClose} className="btn-outline">
            Close
          </button>
        </>
      }
    >
      {/* The run's identity, as the reference chips it above the log. */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
        <Badge tone={JOB_STATUS_TONES[job.status]}>{JOB_STATUS_LABELS[job.status]}</Badge>
        <span className="rounded bg-panel3 px-2 py-1 text-fmuted">Source: {sourceLabel}</span>
        {job.deployment_methods.map((m) => (
          <span
            key={m}
            className={`rounded px-2 py-1 uppercase ${
              m === 'intune' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-300' : 'bg-panel3 text-fmuted'
            }`}
          >
            {m}
          </span>
        ))}
        <span className="rounded bg-panel3 px-2 py-1 text-fmuted">Created: {dateTime(job.created_at)}</span>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-300">
          {error}
        </div>
      )}

      <DecisionPanel job={job} onDone={onJobUpdated} />

      {job.recovery && (() => {
        // The platform packages the failed job again with a different strategy; each job links
        // to the other.
        const other = job.recovery.recovery_job_id ?? job.recovery.retry_of_job_id
        const label = job.recovery.recovery_job_id ? 'View the new job' : 'View the failed job'
        const tone = job.recovery.state === 'failed'
          ? 'border-red-500/25 bg-red-500/10 text-red-600 dark:text-red-300'
          : job.recovery.state === 'completed'
            ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300'
            : 'border-blue-500/25 bg-blue-500/10 text-blue-600 dark:text-blue-300'
        return (
          <div className={`mb-4 rounded-xl border px-4 py-3 ${tone}`}>
            <h4 className="text-sm font-semibold">{job.recovery.message}</h4>
            {job.recovery.recovery_error && (
              <p className="mt-1 break-words text-sm">{errorSummary(job.recovery.recovery_error)}</p>
            )}
            {other && (
              <p className="mt-1 text-sm">
                {onOpenJob ? (
                  <button type="button" className="underline" onClick={() => onOpenJob(other)}>
                    {label} ({other.substring(0, 8)})
                  </button>
                ) : (
                  <span>
                    {label}: <span className="font-mono">{other.substring(0, 8)}</span>
                  </span>
                )}
              </p>
            )}
          </div>
        )
      })()}

      {tabs.length > 1 && (
        <div className="mb-4">
          <Tabs tabs={tabs} active={activeTab} onChange={(k) => setActiveTab(k as TabType)} />
        </div>
      )}

      {activeTab === 'pipeline' && (
        <ol className="space-y-2">
          {jobPipeline(job, artifacts, reportContent).map((step) => (
            <PipelineRow key={step.label} step={step} />
          ))}
        </ol>
      )}

      {activeTab === 'logs' && logContent && (
        <div>
          <div className="mb-2 flex justify-end">
            <button onClick={handleCopyLogs} className="btn-outline btn-sm">
              {copiedToClipboard ? (
                <>
                  <CheckIcon className="h-4 w-4 text-emerald-500" />
                  Copied
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy Logs
                </>
              )}
            </button>
          </div>
          <div className="max-h-[52vh] overflow-auto rounded-lg border border-line bg-base p-3">
            <pre className="whitespace-pre-wrap break-all font-mono text-[12px] leading-relaxed text-fmuted">
              {logContent}
            </pre>
          </div>
        </div>
      )}

      {activeTab === 'report' && reportContent && (
        <div className="max-h-[52vh] overflow-auto rounded-lg border border-line bg-panel3 p-4">
          <pre className="whitespace-pre-wrap break-words font-sans text-sm text-ftext">{reportContent}</pre>
        </div>
      )}

      {activeTab === 'details' && (
        <>
          {!isTerminalStatus(job.status) && (
            <div className="mb-6">
              <div className="mb-2 flex justify-between text-sm text-fmuted">
                <span>Progress</span>
                <span className="tabular-nums">{job.progress_pct}%</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-panel3">
                <div
                  className="h-full rounded-full bg-accent transition-all duration-300"
                  style={{ width: `${job.progress_pct}%` }}
                />
              </div>
            </div>
          )}

          {job.error_message && (() => {
            // A completed job whose message mentions a failed uninstall test is a WARNING, not an
            // error: the package exists and deploys. Only a failed job is red.
            const isPartialSuccess = job.status === 'completed' && (
              job.error_message.toLowerCase().includes('partial success') ||
              job.error_message.toLowerCase().includes('uninstall') ||
              job.error_message.toLowerCase().includes('with warnings')
            )
            const isFailed = job.status === 'failed'
            const tone = isPartialSuccess
              ? 'border-amber-500/25 bg-amber-500/10 text-amber-600 dark:text-amber-300'
              : isFailed
                ? 'border-red-500/25 bg-red-500/10 text-red-600 dark:text-red-300'
                : 'border-line bg-panel3 text-fmuted'

            const detail = isPartialSuccess
              ? 'The package was created but some tests failed (e.g., the uninstall test). Review the report before deploying to Intune.'
              : errorSummary(job.error_message)
            // The raw message is the tail of the run. It belongs here in full, but under the
            // cause and in a block that scrolls, not as a paragraph that pushes the page down.
            const raw = !isPartialSuccess && job.error_message.trim() !== detail

            return (
              <div className={`mb-6 rounded-xl border px-4 py-3 ${tone}`}>
                <h4 className="text-sm font-semibold">
                  {isPartialSuccess ? 'Warning' : isFailed ? 'Error' : 'Note'}
                </h4>
                <p className="mt-1 break-words text-sm">{detail}</p>
                {raw && (
                  <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-line bg-base p-2 font-mono text-[11px] leading-relaxed text-fmuted">
                    {job.error_message}
                  </pre>
                )}
              </div>
            )
          })()}

          <h3 className="mb-3 text-sm font-semibold text-ftext">Job Information</h3>
          <dl className="mb-6 grid grid-cols-2 gap-4 text-sm">
            <Detail label="Job ID" mono>{job.job_id}</Detail>
            <Detail label="Package Source">{sourceLabel}</Detail>
            <Detail label="Source Reference" mono span2>{job.source_reference}</Detail>
            <Detail label="Deployment Methods">
              <span className="flex gap-1">
                {job.deployment_methods.map((m) => (
                  <span
                    key={m}
                    className="rounded bg-panel3 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-fmuted"
                  >
                    {m}
                  </span>
                ))}
              </span>
            </Detail>
            <Detail label="Retry Count">{job.retry_count}</Detail>
            <Detail label="Created">{dateTime(job.created_at)}</Detail>
            <Detail label="Started">{dateTime(job.started_at)}</Detail>
            <Detail label="Completed">{dateTime(job.completed_at)}</Detail>
            {job.result_app_id && (
              <Detail label="Result App">
                <a href={`/apps/${job.result_app_id}`} className="btn-outline btn-sm mt-0.5">
                  View App
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              </Detail>
            )}
          </dl>

          {isTerminalStatus(job.status) && (
            <>
              <h3 className="mb-3 text-sm font-semibold text-ftext">Downloadable Artifacts</h3>
              {loadingArtifacts ? (
                <p className="text-sm text-ffaint">Loading artifacts…</p>
              ) : packages.length === 0 ? (
                <p className="text-sm text-ffaint">No package artifacts available</p>
              ) : (
                <div className="space-y-2">
                  {packages.map((artifact) => {
                    const Icon = ARTIFACT_ICONS[artifact.artifact_type] ?? DocumentIcon
                    return (
                      <div
                        key={artifact.artifact_id}
                        className="flex items-center justify-between gap-3 rounded-lg border border-line bg-panel2 p-3"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <Icon className="h-5 w-5 shrink-0 text-fmuted" />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-ftext">
                              {artifact.artifact_type === 'full_package'
                                ? 'Full Package (All Files)'
                                : artifact.file_name}
                            </p>
                            <p className="text-xs text-ffaint">
                              {formatFileSize(artifact.file_size)} —{' '}
                              {artifact.artifact_type === 'full_package'
                                ? 'Complete package zip'
                                : artifact.artifact_type.replace('_', ' ')}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownload(artifact)}
                          disabled={downloading === artifact.artifact_id}
                          className="btn-ghost btn-sm"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          {downloading === artifact.artifact_id ? 'Downloading…' : 'Download'}
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}
    </Modal>
  )
}

/** One pipeline step: its state as a glyph, then what it did — the reference's `<ol>`. */
function PipelineRow({ step }: { step: PipelineStep }) {
  const reached = step.state !== 'pending' && step.state !== 'skipped'
  return (
    <li className="flex items-center gap-3">
      {step.state === 'done' ? (
        <CheckCircleIcon className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
      ) : step.state === 'running' ? (
        <Spinner className="h-5 w-5 shrink-0 text-blue-600 dark:text-blue-300" />
      ) : step.state === 'failed' ? (
        <XCircleIcon className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
      ) : step.state === 'warning' ? (
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
      ) : step.state === 'skipped' ? (
        <MinusCircleIcon className="h-5 w-5 shrink-0 text-ffaint" />
      ) : (
        <span className="h-5 w-5 shrink-0 rounded-full border-2 border-line2" />
      )}
      <span className={`text-sm ${reached ? 'text-ftext' : 'text-ffaint'}`}>{step.label}</span>
      {step.note && <span className="text-[12px] text-ffaint">— {step.note}</span>}
    </li>
  )
}

/** One labelled field in the job's information grid. */
function Detail({ label, children, mono, span2 }: {
  label: string
  children: React.ReactNode
  mono?: boolean
  span2?: boolean
}) {
  return (
    <div className={span2 ? 'col-span-2' : undefined}>
      <dt className="text-ffaint">{label}</dt>
      <dd className={`mt-1 text-ftext ${mono ? 'break-all font-mono text-xs' : ''}`}>{children}</dd>
    </div>
  )
}
